import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../redux/user/action";
import { wheelApi, WheelApiError } from "../services/wheel-of-luck";
import { WheelDetail, WheelEvent, WheelSlot, WheelSpinRequest, WheelSpinResult } from "../types/wheel-of-luck";
import { createSpinRequest, wheelErrors } from "../utils/wheel-of-luck";
import Network from "../utils/Network";
import { useAppTranslation } from "../i18n/useAppTranslation";

type Phase = "idle" | "requesting" | "animating";
type Clock = { server: number; received: number };
// A client message is kept as its key and translated when shown, so it follows
// a language change; a server message arrives already in the player's language.
type ErrorMessage = { key: string } | { text: string };

export default function useLuckyWheel(enabled: boolean, historicalKeyId?: number) {
  const { t } = useAppTranslation(["wheel"]);
  const dispatch = useDispatch();
  const playerId = useSelector((state: any) => state.userInfo?.id);
  const [events, setEvents] = useState<WheelEvent[]>([]);
  const [wheel, setWheel] = useState<WheelDetail | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<ErrorMessage | null>(null);
  const [pending, setPending] = useState<WheelSpinRequest | null>(null);
  const [spinResult, setSpinResult] = useState<WheelSpinResult | null>(null);
  const [lastReward, setLastReward] = useState<WheelSlot | null>(null);
  const [clock, setClock] = useState<Clock | null>(null);
  const [now, setNow] = useState(performance.now());
  const busy = useRef(false);
  const selectedIdRef = useRef<number | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const pendingRef = useRef<WheelSpinRequest | null>(null);
  const resultRef = useRef<WheelSpinResult | null>(null);
  const resultReceived = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; generation.current += 1; };
  }, []);

  const storageKey = useCallback((id: number) => `wheel-spin:${playerId}:${id}`, [playerId]);
  const savePending = useCallback((request: WheelSpinRequest | null, wheelId: number) => {
    pendingRef.current = request;
    setPending(request);
    try {
      if (request) sessionStorage.setItem(storageKey(wheelId), JSON.stringify(request));
      else sessionStorage.removeItem(storageKey(wheelId));
    } catch { /* In-memory retry still works when browser storage is unavailable. */ }
  }, [storageKey]);

  const applyWheel = useCallback((data: WheelDetail, received = performance.now()) => {
    setWheel(data);
    setClock({ server: Date.parse(data.serverTime), received });
    setNow(performance.now());
  }, []);

  const load = useCallback(async (id?: number | null, keepError = false) => {
    if (busy.current) return;
    const version = ++generation.current;
    setLoading(true);
    if (!keepError) setError(null);
    try {
      const list = historicalKeyId ? [] : await wheelApi.list();
      if (!mounted.current || version !== generation.current) return;
      setEvents(list);
      const nextId = id && list.some(event => event.playerWheelOfLuckId === id)
        ? id : list[0]?.playerWheelOfLuckId;
      const data = historicalKeyId
        ? await wheelApi.historical(historicalKeyId)
        : nextId ? await wheelApi.open(nextId) : null;
      if (!mounted.current || version !== generation.current) return;
      setLastReward(null);
      selectedIdRef.current = data?.playerWheelOfLuckId ?? null;
      setSelectedId(selectedIdRef.current);
      if (data) {
        applyWheel(data);
        let restored: WheelSpinRequest | null = null;
        try {
          const raw = sessionStorage.getItem(storageKey(data.playerWheelOfLuckId));
          const value = raw ? JSON.parse(raw) : null;
          if (value?.playerWheelOfLuckId === data.playerWheelOfLuckId &&
            (value.paymentMethod === 1 || value.paymentMethod === 2) &&
            typeof value.clientRequestId === "string" && value.clientRequestId) restored = value;
        } catch { /* Ignore unavailable storage. */ }
        // Retain a pending request even when sessionStorage is disabled.
        if (pendingRef.current?.playerWheelOfLuckId === data.playerWheelOfLuckId) restored = pendingRef.current;
        pendingRef.current = restored;
        setPending(restored);
      } else {
        setWheel(null);
        setClock(null);
        pendingRef.current = null;
        setPending(null);
      }
    } catch (cause) {
      if (mounted.current && version === generation.current) {
        const failure = cause as WheelApiError;
        // Without a status the failure is the client's own (no connection, a
        // malformed response), and its English text is not for the player.
        setError(wheelErrors[failure.code] ? { key: wheelErrors[failure.code] }
          : failure.status && failure.message ? { text: failure.message } : { key: "wheel:errors.loadFailed" });
        // Do not leave an old wheel actionable after a failed refresh.
        setWheel(null);
      }
    } finally {
      if (mounted.current && version === generation.current) setLoading(false);
    }
  }, [applyWheel, historicalKeyId, storageKey]);

  useEffect(() => {
    if (enabled) void load(selectedIdRef.current);
    else generation.current += 1;
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => setNow(performance.now()), 1000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  const refreshPlayer = useCallback(async () => {
    const response = await new Network().CallApi({ url: "/player", method: "get" });
    if (response.ok && response.data && mounted.current) dispatch(setUserInfo(response.data));
  }, [dispatch]);

  const spin = async (paymentMethod: 1 | 2) => {
    if (!enabled || !wheel || loading || busy.current) return;
    const retry = pendingRef.current;
    if (retry && retry.paymentMethod !== paymentMethod) return;
    const serverNow = clock ? clock.server + performance.now() - clock.received : NaN;
    const eventEnded = wheel.eventEnded || Boolean(wheel.eventEndAt && serverNow >= Date.parse(wheel.eventEndAt));
    if (!retry && (wheel.allSlotsClaimed || wheel.slots.every(slot => slot.claimed) ||
      (eventEnded && !wheel.isHistorical) ||
      (paymentMethod === 1 && (!wheel.keyAccepted || wheel.validKeyCount < 1)) ||
      (paymentMethod === 2 && (!wheel.clegAccepted || eventEnded || wheel.isHistorical)))) return;
    // Construct the ID before locking so an unavailable crypto provider cannot
    // strand the controls in the requesting state.
    const request = retry || createSpinRequest(wheel.playerWheelOfLuckId, paymentMethod);
    busy.current = true; // Synchronous lock also covers two clicks in one render.
    setPhase("requesting");
    setError(null);
    setLastReward(null);
    savePending(request, wheel.playerWheelOfLuckId);
    try {
      const result = await wheelApi.spin(request);
      if (!mounted.current) return;
      resultRef.current = result;
      resultReceived.current = performance.now();
      setSpinResult(result);
      setPhase("animating");
      // Keep the original slots visible until the winning animation finishes.
    } catch (cause) {
      if (!mounted.current) return;
      const failure = cause instanceof WheelApiError ? cause : new WheelApiError("Connection interrupted.");
      busy.current = false;
      setPhase("idle");
      if (failure.uncertain) {
        setError({ key: "wheel:errors.spinUnconfirmed" });
        return;
      }
      savePending(null, request.playerWheelOfLuckId);
      setError(wheelErrors[failure.code] ? { key: wheelErrors[failure.code] } : { text: failure.message });
      await load(request.playerWheelOfLuckId, true);
      if (failure.code === "InsufficientCleg" || failure.code === "DuplicateRequest") void refreshPlayer();
    }
  };

  const completeSpin = useCallback(() => {
    const result = resultRef.current;
    if (!result) return;
    resultRef.current = null;
    applyWheel(result.wheel, resultReceived.current);
    setLastReward(result.grantedReward);
    setEvents(previous => previous.map(event => event.playerWheelOfLuckId === result.wheel.playerWheelOfLuckId ? result.wheel : event));
    savePending(null, result.wheel.playerWheelOfLuckId);
    setSpinResult(null);
    setPhase("idle");
    busy.current = false;
    void refreshPlayer();
  }, [applyWheel, refreshPlayer, savePending]);

  useEffect(() => {
    // Browser Back can dismiss the route independently of the modal close
    // button. A hidden animation must not leave this session locked forever.
    if (!enabled && phase === "animating") completeSpin();
  }, [enabled, phase, completeSpin]);

  const selectWheel = (id: number) => {
    if (busy.current || pendingRef.current) return;
    setLastReward(null);
    setWheel(null);
    void load(id);
  };

  return {
    events, wheel, selectedId, loading, phase, pending, spinResult, lastReward,
    error: error && ("key" in error ? t(error.key) : error.text),
    serverNow: clock ? clock.server + now - clock.received : NaN,
    spin, completeSpin, selectWheel,
    dismissReward: () => setLastReward(null),
    reload: () => load(selectedId),
  };
}
